<template>
  <div class="page-container">
    <div class="page-header">
      <div class="page-title logger-title-wrap">
        <div>
          로거 센서 관리
          <span class="count-text">총 {{ resolvedTotalCount }} 건</span>
        </div>

        <button
          type="button"
          class="unassigned-btn"
          :class="{ active: showUnassignedOnly }"
          @click="onClickUnassignedOnly"
        >
          미등록 로거 전체보기
          <span class="unassigned-count">{{ unassignedCount }}건</span>
        </button>
      </div>

      <div class="header-buttons logger-search-wrap">
        <select v-model="searchType" class="logger-search-select">
          <option value="all">전체</option>
          <option value="siteName">현장</option>
        </select>

        <input
          v-model.trim="searchKeyword"
          class="logger-search-input"
          type="text"
          placeholder="로거명, 라인번호, 현장명 검색"
          @keyup.enter="onSearch"
        />

        <button class="btn secondary sm" @click="onSearch" :disabled="loading">
          검색
        </button>

        <button class="btn ghost sm" @click="onRefresh" :disabled="loading">
          새로고침
        </button>

        <button class="btn primary d-none" @click="onClickCreate">신규 등록</button>
      </div>
    </div>

    <div class="content-wrapper">

      <!-- 현장 빠른 필터 -->
      <div class="site-tab-section">
        <button
          class="site-tab-btn"
          :class="{ active: selectedSiteId === null }"
          @click="onClickSiteTab(null)"
        >
          전체
        </button>

        <button
          v-for="site in siteOptions"
          :key="site.id"
          class="site-tab-btn"
          :class="{ active: Number(selectedSiteId) === Number(site.id) }"
          @click="onClickSiteTab(site.id)"
        >
          {{ site.name }}
        </button>
      </div>

      <div class="summary-section">

        <!-- 차트 카드 1 -->
        <div class="chart-card">
          <div class="chart-card-header">
            <span class="chart-index">1</span>
            <span class="chart-title">전체 장비 설치 현황</span>
          </div>

          <div class="chart-body">
            <div class="chart-canvas-wrap">
              <div class="chart-canvas">
                <canvas ref="installChartCanvas"></canvas>
              </div>
            </div>

            <div class="chart-meta">
              <div class="meta-total">설치 {{ totalInstalled }} 대</div>

              <ul class="legend-list">
                <li v-for="item in installLegend" :key="item.label" class="legend-item">
                  <span class="legend-color" :style="{ backgroundColor: item.color }"></span>
                  <span class="legend-label">{{ item.label }}</span>
                  <span class="legend-value">{{ item.value }}/{{ totalInstalled }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- 차트 카드 2 -->
        <div class="chart-card">
          <div class="chart-card-header">
            <span class="chart-index">2</span>
            <span class="chart-title">전체 장비 통신 상태</span>
          </div>

          <div class="chart-body">
            <div class="chart-canvas-wrap">
              <div class="chart-canvas">
                <canvas ref="communicationChartCanvas"></canvas>
              </div>
            </div>

            <div class="chart-meta">
              <div class="meta-total">설치 {{ totalInstalled }} 대</div>

              <ul class="legend-list">
                <li v-for="item in communicationLegend" :key="item.label" class="legend-item">
                  <span class="legend-color" :style="{ backgroundColor: item.color }"></span>
                  <span class="legend-label">{{ item.label }}</span>
                  <span class="legend-value">{{ item.value }}/{{ totalInstalled }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- 차트 카드 3 -->
        <div class="chart-card">
          <div class="chart-card-header">
            <span class="chart-index">3</span>
            <span class="chart-title">전체 장비 운영 현황</span>
          </div>

          <div class="chart-body">
            <div class="chart-canvas-wrap">
              <div class="chart-canvas">
                <canvas ref="operationChartCanvas"></canvas>
              </div>
            </div>

            <div class="chart-meta">
              <div class="meta-total">운영 {{ totalOperating }} 대</div>

              <ul class="legend-list">
                <li v-for="item in operationLegend" :key="item.label" class="legend-item">
                  <span class="legend-color" :style="{ backgroundColor: item.color }"></span>
                  <span class="legend-label">{{ item.label }}</span>
                  <span class="legend-value">{{ item.value }}/{{ totalOperating }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

      </div>

      <!-- 테이블 -->
      <div class="table-section">
        <table v-if="tableRows.length" class="data-table logger-table">
          <thead>
            <tr>
              <th>업체</th>
              <th>현장</th>
              <th>라인번호</th>
              <th>업데이트일</th>
              <th>상태</th>
              <th class="col-actions">작업</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="row in tableRows" :key="row.id">
              <td class="td-ellipsis" :title="row.agencyName || '-'">
                {{ row.agencyName || '-' }}
              </td>

              <td class="td-ellipsis" :title="row.siteName || '-'">
                {{ row.siteName || '-' }}
              </td>

              <td>{{ row.lineNumber || '-' }}</td>

              <td>{{ formatDateTime(row.updatedAt) }}</td>

              <td>
                <span
                  class="status-pill"
                  :class="isRegistered(row) ? 'is-normal' : 'is-unregistered'"
                >
                  {{ isRegistered(row) ? '등록' : '미등록' }}
                </span>
              </td>

              <td class="td-actions">
                <div class="row-actions">
                  <button
                    class="logger-link-btn"
                    :class="isRegistered(row)
                      ? 'is-registered'
                      : 'is-unregistered'"
                    @click="openLoggerSiteModal(row)"
                  >
                    {{ isRegistered(row) ? '현장변경' : '현장연동' }}
                  </button>

                  <button class="btn xs primary" @click="onClickDetail(row)">
                    상세보기
                  </button>

                  <button class="btn xs secondary" @click="onClickSensors(row)">
                    센서목록
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-else class="table-empty">
          표시할 로거 데이터가 없습니다
        </div>

        <PaginationBar
          :currentPage="currentPage"
          :totalPages="totalPages"
          :groupSize="groupSize"
          @change-page="goToPage"
        />


      </div>

    </div>

    <!-- 로거 현장 연동/변경 모달 -->
    <div v-if="siteLinkModalOpen" class="modal-backdrop">
      <div class="logger-site-modal">
        <div class="modal-header">
          <div class="modal-title">
            {{ isRegistered(siteLinkTarget) ? '현장변경' : '현장연동' }}
          </div>
          <button class="modal-close" @click="closeLoggerSiteModal">×</button>
        </div>

        <div class="modal-body">

          <div class="modal-row">
            <label class="modal-label">로거번호</label>

            <div class="logger-number-box">
              {{ siteLinkForm.lineNumber || '-' }}
            </div>
          </div>

          <div class="modal-row">
            <label class="modal-label">현장</label>

            <select
              v-model="siteLinkForm.siteId"
              class="modal-select"
              @change="onChangeModalSite"
            >
              <option value="">현장을 선택하세요</option>

              <option
                v-for="site in siteOptions"
                :key="site.id"
                :value="String(site.id)"
              >
                {{ site.name }}
              </option>
            </select>
          </div>

          <div class="modal-row">
            <label class="modal-label">현장 대리점</label>

            <select
              v-model="siteLinkForm.agencyId"
              class="modal-select"
            >
              <option value="">대리점 없음</option>

              <option
                v-for="agency in agencyOptions"
                :key="agency.id"
                :value="String(agency.id)"
              >
                {{ agency.name }}
              </option>
            </select>
          </div>

          <div class="modal-row">
            <label class="modal-label">현장 발주처</label>

            <select
              v-model="siteLinkForm.clientId"
              class="modal-select"
            >
              <option value="">발주처 없음</option>

              <option
                v-for="client in clientOptions"
                :key="client.id"
                :value="String(client.id)"
              >
                {{ client.name }}
              </option>
            </select>
          </div>

        </div>

        <div class="modal-actions">
          <button class="btn ghost" @click="closeLoggerSiteModal" :disabled="savingSiteLink">
            취소
          </button>
          <button class="btn primary" @click="saveLoggerSiteLink" :disabled="savingSiteLink">
            {{ savingSiteLink ? '저장중...' : '저장' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'
import PaginationBar from '@/components/admin/PaginationBar.vue'
import {
  Chart,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js'

Chart.register(
  ArcElement,
  Tooltip,
  Legend
)

export default {
  name: 'LoggerList',
  emits: ['create', 'detail', 'sensors', 'delete'],
  components: {
    PaginationBar
  },

  data() {
    return {
      tableRows: [],
      totalCount: 0,
      loading: false,

      installChart: null,
      communicationChart: null,
      operationChart: null,

      installLegend: [
        { label: '데이터 없음', value: 0, color: '#d1d5db' }
      ],

      communicationLegend: [
        { label: '정상', value: 0, color: '#4a78c9' },
        { label: '통신 두절', value: 0, color: '#f28c28' }
      ],

      operationLegend: [
        { label: '정상', value: 0, color: '#31c36b' },
        { label: '이상 감지', value: 0, color: '#ff2d2d' }
      ],

      siteOptions: [],
      selectedSiteId: null,
      searchType: 'all',
      searchKeyword: '',

      currentPage: 0,
      size: 10,
      totalPages: 0,
      groupSize: 5,

      unassignedCount: 0,
      showUnassignedOnly: false,

      siteLinkModalOpen: false,
      siteLinkTarget: null,
      savingSiteLink: false,

      agencyOptions: [],
      clientOptions: [],

      siteLinkForm: {
        loggerId: null,
        lineNumber: '',
        siteId: '',
        agencyId: '',
        clientId: ''
      }
    }
  },

  computed: {
    resolvedTotalCount() {
      return this.totalCount || this.tableRows.length
    },

    totalInstalled() {
      return this.installLegend.reduce((sum, item) => sum + Number(item.value || 0), 0)
    },

    totalOperating() {
      return this.operationLegend.reduce((sum, item) => sum + Number(item.value || 0), 0)
    }
  },

  async mounted() {
    await this.initializePage()
  },

  beforeUnmount() {
    this.destroyAllCharts()
  },

  methods: {
    async initializePage() {
      this.loading = true

      try {
        await Promise.all([
          this.fetchSiteOptions(),
          this.fetchAgencyOptions(),
          this.fetchClientOptions(),
          this.fetchLoggerList(),
          this.fetchOperationSummary(),
          this.fetchUnassignedCount()
        ])

        this.renderAllCharts()
      } catch (e) {
        console.error('LoggerList initializePage error', e)
      } finally {
        this.loading = false
      }
    },

    async fetchLoggerList() {
      try {
        const params = {
          page: this.currentPage,
          size: this.size,
          orderBy: 'updatedAtDesc',
          deleted: false
        }

        if (this.showUnassignedOnly) {
          params.unassigned = true
        }

        if (this.selectedSiteId) {
          params.siteId = this.selectedSiteId
        }

        if (this.searchKeyword) {
          if (this.searchType === 'siteName') {
            params.siteName = this.searchKeyword
          } else {
            params.keyword = this.searchKeyword
          }
        }

        const response = await api.get('/app/sensorLogger/list', { params })
        const pageData = response.data?.result

        this.tableRows = Array.isArray(pageData?.content)
          ? pageData.content
          : []

        this.totalCount = Number(pageData?.totalElements ?? this.tableRows.length)
        this.totalPages = Number(pageData?.totalPages ?? 0)
        this.currentPage = Number(pageData?.number ?? this.currentPage)
      } catch (error) {
        console.error('로거 목록 조회 실패', error)
        this.tableRows = []
        this.totalCount = 0
        this.totalPages = 0
      }
    },

    async fetchOperationSummary() {
      try {
        const params = {}

        if (this.selectedSiteId) {
          params.siteId = this.selectedSiteId
        }

        const response = await api.get('/app/sensorLogger/summary', { params })
        const result = response?.data?.result || {}

        this.installLegend = this.normalizeLegendItems(
          result?.installSummary?.items,
          [
            { label: '데이터 없음', value: 0, color: '#d1d5db' }
          ]
        )

        this.communicationLegend = this.normalizeLegendItems(
          result?.communicationSummary?.items,
          [
            { label: '정상', value: 0, color: '#4a78c9' },
            { label: '통신 두절', value: 0, color: '#f28c28' }
          ]
        )

        this.operationLegend = this.normalizeLegendItems(
          result?.operationSummary?.items,
          [
            { label: '정상', value: 0, color: '#31c36b' },
            { label: '이상 감지', value: 0, color: '#ff2d2d' }
          ]
        )
      } catch (error) {
        console.error('로거 요약 조회 실패', error)

        this.installLegend = [
          { label: '데이터 없음', value: 0, color: '#d1d5db' }
        ]

        this.communicationLegend = [
          { label: '정상', value: 0, color: '#4a78c9' },
          { label: '통신 두절', value: 0, color: '#f28c28' }
        ]

        this.operationLegend = [
          { label: '정상', value: 0, color: '#31c36b' },
          { label: '이상 감지', value: 0, color: '#ff2d2d' }
        ]
      }
    },

    normalizeLegendItems(items, fallbackItems) {
      const list = Array.isArray(items) ? items : []

      const normalized = list.map(item => ({
        label: item.label || '-',
        value: Number(item.value || 0),
        color: item.color || '#94a3b8'
      }))

      return normalized.length ? normalized : fallbackItems
    },

    renderAllCharts() {
      this.destroyAllCharts()
      this.renderInstallChart()
      this.renderCommunicationChart()
      this.renderOperationChart()
    },

    destroyAllCharts() {
      if (this.installChart) {
        this.installChart.destroy()
        this.installChart = null
      }

      if (this.communicationChart) {
        this.communicationChart.destroy()
        this.communicationChart = null
      }

      if (this.operationChart) {
        this.operationChart.destroy()
        this.operationChart = null
      }
    },

    getCommonDoughnutOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        animation: {
          duration: 500
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label(context) {
                const label = context.label || ''
                const value = context.raw || 0
                return `${label}: ${value}`
              }
            }
          }
        }
      }
    },

    createChart(canvasRef, labels, values, colors) {
      const canvas = this.$refs[canvasRef]
      if (!canvas) return null

      const ctx = canvas.getContext('2d')
      if (!ctx) return null

      const hasValue = Array.isArray(values) && values.some(v => Number(v) > 0)

      const safeValues = hasValue ? values : [1]
      const safeLabels = hasValue ? labels : ['데이터 없음']
      const safeColors = hasValue ? colors : ['#d1d5db']

      return new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: safeLabels,
          datasets: [
            {
              data: safeValues,
              backgroundColor: safeColors,
              borderColor: '#ffffff',
              borderWidth: 2,
              hoverOffset: 4
            }
          ]
        },
        options: this.getCommonDoughnutOptions()
      })
    },

    renderInstallChart() {
      this.installChart = this.createChart(
        'installChartCanvas',
        this.installLegend.map(item => item.label),
        this.installLegend.map(item => item.value),
        this.installLegend.map(item => item.color)
      )
    },

    renderCommunicationChart() {
      this.communicationChart = this.createChart(
        'communicationChartCanvas',
        this.communicationLegend.map(item => item.label),
        this.communicationLegend.map(item => item.value),
        this.communicationLegend.map(item => item.color)
      )
    },

    renderOperationChart() {
      this.operationChart = this.createChart(
        'operationChartCanvas',
        this.operationLegend.map(item => item.label),
        this.operationLegend.map(item => item.value),
        this.operationLegend.map(item => item.color)
      )
    },

    formatDateTime(value) {
      if (!value) return '-'

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value

      const yyyy = date.getFullYear()
      const yy = String(yyyy).slice(-2)
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mi = String(date.getMinutes()).padStart(2, '0')
      const ss = String(date.getSeconds()).padStart(2, '0')

      return `${yy}-${mm}-${dd} ${hh}:${mi}:${ss}`
    },

    onClickCreate() {
      this.$emit('create')
    },

    onClickDetail(row) {
      this.$emit('detail', row)
    },

    onClickSensors(row) {
      this.$emit('sensors', row)
    },

    onClickDelete(row) {
      this.$emit('delete', row)
    },

    async fetchSiteOptions() {
      try {
        const response = await api.get('/app/site/myList')

        this.siteOptions = Array.isArray(response.data?.result)
          ? response.data.result
          : []
      } catch (error) {
        console.error('접근 가능 현장 목록 조회 실패', error)
        this.siteOptions = []
      }
    },

    async reloadByCondition() {
      this.loading = true

      try {
        await Promise.all([
          this.fetchLoggerList(),
          this.fetchOperationSummary(),
          this.fetchUnassignedCount()
        ])

        this.renderAllCharts()
      } catch (e) {
        console.error('LoggerList reloadByCondition error', e)
      } finally {
        this.loading = false
      }
    },

    async onClickSiteTab(siteId) {
      this.showUnassignedOnly = false
      this.selectedSiteId = siteId
      this.currentPage = 0
      await this.reloadByCondition()
    },

    async onSearch() {
      this.currentPage = 0
      await this.reloadByCondition()
    },

    async onRefresh() {
      this.showUnassignedOnly = false
      this.selectedSiteId = null
      this.searchType = 'all'
      this.searchKeyword = ''
      this.currentPage = 0
      await this.reloadByCondition()
    },

    async goToPage(pageNo) {
      if (pageNo < 0 || pageNo >= this.totalPages || pageNo === this.currentPage) {
        return
      }

      this.currentPage = pageNo
      await this.reloadByCondition()
    },

    async fetchUnassignedCount() {
      try {
        const response = await api.get('/app/sensorLogger/list', {
          params: {
            page: 0,
            size: 1,
            orderBy: 'updatedAtDesc',
            deleted: false,
            unassigned: true
          }
        })

        const pageData = response.data?.result
        this.unassignedCount = Number(pageData?.totalElements ?? 0)
      } catch (error) {
        console.error('미등록 로거 개수 조회 실패', error)
        this.unassignedCount = 0
      }
    },

    async onClickUnassignedOnly() {
      this.showUnassignedOnly = !this.showUnassignedOnly

      this.selectedSiteId = null
      this.searchType = 'all'
      this.searchKeyword = ''
      this.currentPage = 0

      await this.reloadByCondition()
    },

    isRegistered(row) {
      if (!row) return false

      return !!(
        row.siteId ||
        row.site?.id ||
        row.siteName
      )
    },

    async fetchAgencyOptions() {
      try {
        const response = await api.get('/app/agency/list', {
          params: {
            page: 0,
            size: 1000,
            deleted: false,
            orderBy: 'createdAtDesc'
          }
        })

        const pageData = response.data?.result
        this.agencyOptions = Array.isArray(pageData?.content)
          ? pageData.content
          : []
      } catch (error) {
        console.error('대리점 목록 조회 실패', error)
        this.agencyOptions = []
      }
    },

    async fetchClientOptions() {
      try {
        const response = await api.get('/app/client/list', {
          params: {
            page: 0,
            size: 1000,
            deleted: false,
            orderBy: 'createdAtDesc'
          }
        })

        const pageData = response.data?.result
        this.clientOptions = Array.isArray(pageData?.content)
          ? pageData.content
          : []
      } catch (error) {
        console.error('발주처 목록 조회 실패', error)
        this.clientOptions = []
      }
    },

    async openLoggerSiteModal(row) {
      this.siteLinkTarget = row

      this.siteLinkForm = {
        loggerId: row.id,
        lineNumber: row.lineNumber || '',
        siteId: row.siteId ? String(row.siteId) : '',
        agencyId: '',
        clientId: ''
      }

      this.siteLinkModalOpen = true

      if (row.siteId) {
        await this.loadModalSiteDetail(row.siteId)
      }
    },

    closeLoggerSiteModal() {
      this.siteLinkModalOpen = false
      this.siteLinkTarget = null
      this.savingSiteLink = false

      this.siteLinkForm = {
        loggerId: null,
        lineNumber: '',
        siteId: '',
        agencyId: '',
        clientId: ''
      }
    },

    async onChangeModalSite() {
      if (!this.siteLinkForm.siteId) {
        this.siteLinkForm.agencyId = ''
        this.siteLinkForm.clientId = ''
        return
      }

      await this.loadModalSiteDetail(this.siteLinkForm.siteId)
    },

    async loadModalSiteDetail(siteId) {
      try {
        const response = await api.get('/app/site/detail', {
          params: {
            id: siteId,
            deleted: false
          }
        })

        const site = response.data?.result || {}

        this.siteLinkForm.agencyId = site.agencyId ? String(site.agencyId) : ''
        this.siteLinkForm.clientId = site.clientId ? String(site.clientId) : ''
      } catch (error) {
        console.error('현장 상세 조회 실패', error)
        this.siteLinkForm.agencyId = ''
        this.siteLinkForm.clientId = ''
      }
    },

    async saveLoggerSiteLink() {
      if (!this.siteLinkForm.loggerId) {
        alert('로거 정보가 없습니다.')
        return
      }

      const selectedSiteId = this.siteLinkForm.siteId
        ? Number(this.siteLinkForm.siteId)
        : null

      this.savingSiteLink = true

      try {
        if (selectedSiteId) {
          const formData = new FormData()

          formData.append('paramsObj', JSON.stringify({
            id: selectedSiteId,
            agencyId: this.siteLinkForm.agencyId
              ? Number(this.siteLinkForm.agencyId)
              : null,
            clientId: this.siteLinkForm.clientId
              ? Number(this.siteLinkForm.clientId)
              : null,
            siteMainImagesChanged: false
          }))

          await api.post('/app/site/update', formData)
        }

        await api.post('/app/sensorLogger/update', {
          id: this.siteLinkForm.loggerId,
          siteId: selectedSiteId
        })

        alert(
          selectedSiteId
            ? '현장 연동 정보가 저장되었습니다.'
            : '현장 연동이 해제되었습니다.'
        )

        this.closeLoggerSiteModal()

        await Promise.all([
          this.fetchSiteOptions(),
          this.fetchLoggerList(),
          this.fetchUnassignedCount(),
          this.fetchOperationSummary()
        ])

        this.renderAllCharts()
      } catch (error) {
        console.error('로거 현장 연동 저장 실패', error)
        alert('현장 연동 저장에 실패했습니다.')
      } finally {
        this.savingSiteLink = false
      }
    }
  }
}
</script>

<style scoped>
@import "@/styles/admin/admin-layout.css";
@import "@/styles/admin/admin-button.css";
@import "@/styles/admin/admin-table.css";
@import "@/styles/admin/admin-pagination.css";

/* =========================
   📊 SUMMARY (차트 영역)
========================= */

.summary-section {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 18px;
  align-items: stretch;
}

.chart-card {
  display: flex;
  flex-direction: column;

  border-radius: 12px;
  background: #ffffff;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
  transition: box-shadow 0.2s ease;

  min-height: 360px;
}

.chart-card:hover {
  box-shadow: 0 6px 18px rgba(0,0,0,0.08);
}

.chart-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.chart-index {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #3b82f6;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.chart-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}

.chart-body {
  flex: 1;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 18px;

  min-height: 0;
  overflow: hidden;
}

.chart-canvas-wrap {
  width: 150px;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-canvas {
  position: relative;
  width: 140px;
  height: 140px;
}

.chart-meta {
  flex: 1;
  min-width: 0;
  min-height: 0;

  display: flex;
  flex-direction: column;
  justify-content: center;
}

.meta-total {
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 700;
  color: #374151;
}

.legend-list {
  list-style: none;
  padding: 0;
  margin: 0;

  max-height: 220px;
  overflow-y: auto;
  overflow-x: hidden;

  padding-right: 6px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
  color: #4b5563;
}

.legend-color {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.legend-label {
  min-width: 72px;
}

.legend-value {
  color: #111827;
  font-weight: 600;
}

/* =========================
   📋 TABLE
========================= */

.table-section {
  overflow: auto;
}

.logger-table {
  width: 100%;
  min-width: 1120px;
  table-layout: fixed;
}

/* 전 컬럼 말줄임 금지, 긴 텍스트는 줄바꿈 */
.logger-table th,
.logger-table td {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  word-break: keep-all;
  overflow-wrap: anywhere;
  line-height: 1.45;
  vertical-align: middle;
}

/* =========================
   TABLE HOVER ACTIVE
========================= */

.logger-table tbody tr {
  transition: background-color 0.15s ease;
}

/* 행 전체 hover */
.logger-table tbody tr:hover td {
  background: #f9fafb;
}

/* 좌측 active 라인 즉시 반응 */
.logger-table tbody tr:hover td:first-child {
  box-shadow: inset 4px 0 0 #e58a13;
}

/* 현재 테이블 컬럼 폭 */
.logger-table th:nth-child(1),
.logger-table td:nth-child(1) {
  width: 190px;
}

.logger-table th:nth-child(2),
.logger-table td:nth-child(2) {
  width: 240px;
}

.logger-table th:nth-child(3),
.logger-table td:nth-child(3) {
  width: 130px;
}

.logger-table th:nth-child(4),
.logger-table td:nth-child(4) {
  width: 170px;
}

.logger-table th:nth-child(5),
.logger-table td:nth-child(5) {
  width: 100px;
}

.logger-table th:nth-child(6),
.logger-table td:nth-child(6) {
  width: 260px;
}

/* 예전 col class가 남아있을 경우 대비 */
.col-id { width: 70px; }
.col-name { width: 180px; }
.col-line { width: 130px; }
.col-date { width: 170px; }
.col-site { width: 240px; }
.col-register { width: 110px; }
.col-location { width: 140px; }
.col-status { width: 100px; }
.col-actions { width: 260px; }

.td-ellipsis {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 76px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-pill.is-normal {
  background: #eaf8ef;
  color: #1f9d55;
}

.status-pill.is-warning {
  background: #fff4df;
  color: #c97a10;
}

.status-pill.is-unregistered {
  background: #fff1f2;
  color: #dc2626;
}

.td-actions {
  text-align: center;
}

.row-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: nowrap;
  white-space: nowrap;
}

/* =========================
   🔍 LOGGER SEARCH / FILTER
========================= */

.logger-search-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.logger-search-select,
.logger-search-input {
  height: 34px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 13px;
  background: #ffffff;
}

.logger-search-select {
  width: 100px;
}

.logger-search-input {
  width: 260px;
}

.site-tab-section {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.site-tab-btn {
  height: 36px;
  padding: 0 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.site-tab-btn:hover {
  background: #f8fafc;
  border-color: #93c5fd;
}

.site-tab-btn.active {
  background: #2563eb;
  border-color: #2563eb;
  color: #ffffff;
}

.logger-title-wrap {
  display: flex;
  align-items: center;
  gap: 14px;
}

.unassigned-btn {
  height: 32px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  color: #374151;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.unassigned-btn:hover {
  border-color: #2563eb;
  color: #2563eb;
}

.unassigned-btn.active {
  background: #2563eb;
  border-color: #2563eb;
  color: #ffffff;
}

.unassigned-count {
  margin-left: 6px;
  font-size: 12px;
  opacity: 0.85;
}

/* =========================
   🔗 LOGGER SITE MODAL
========================= */

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.logger-site-modal {
  width: 520px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.28);
  padding: 24px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 14px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 18px;
  font-weight: 800;
  color: #111827;
}

.modal-close {
  border: none;
  background: transparent;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  color: #6b7280;
}

/*
 * 기존 form-row 구조 제거 안하고
 * modal-row 구조만 추가
 */
.modal-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/*
 * label + input 한 줄 정렬
 */
.modal-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

.modal-label {
  width: 110px;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  text-align: left;
}

.modal-select {
  flex: 1;
  height: 40px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 13px;
  background: #ffffff;
  color: #111827;
  transition: border-color 0.18s ease;
}

.modal-select:focus {
  outline: none;
  border-color: #3b82f6;
}

.logger-number-box {
  flex: 1;
  height: 40px;
  display: flex;
  align-items: center;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
  color: #111827;
  font-size: 13px;
  font-weight: 700;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}

/* =========================
   🔗 현장연동 / 현장변경 버튼
========================= */

.logger-link-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  min-width: 46px;
  height: 28px;
  padding: 0 10px;

  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;

  transition: all 0.18s ease;
}

.logger-link-btn.is-registered {
  background: #eff6ff;
  border: 1px solid #60a5fa;
  color: #2563eb;
}

.logger-link-btn.is-registered:hover {
  background: #dbeafe;
  border-color: #2563eb;
  color: #1d4ed8;

  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(37, 99, 235, 0.25);
}

.logger-link-btn.is-unregistered {
  background: #fff1f2;
  border: 1px solid #fda4af;
  color: #dc2626;
}

.logger-link-btn.is-unregistered:hover {
  background: #ffe4e6;
  border-color: #ef4444;
  color: #b91c1c;

  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(239, 68, 68, 0.28);
}

.logger-link-btn:active {
  transform: translateY(0);
}

@media (max-width: 1200px) {
  .summary-section {
    grid-template-columns: 1fr;
  }
}
</style>